import { decodeHtmlEntities } from "../../../../../../common/helpers/decodeHtmlEntities";
import type {
  RawProduct,
  RawProductTranslations,
} from "../../../../../../types";

interface TranslatedTexts {
  name: string;
  description: string;
  shortDescription: string;
}

/**
 * A pure helper function to get translated text fields for a product.
 * It falls back to the product's default language fields if a translation is missing.
 *
 * @param product The raw product object from the API.
 * @param translations The translations object from the product's extensions.
 * @param currentLanguage The current language code (e.g., 'en').
 * @param shouldTranslate A boolean indicating if translations should be applied.
 * @returns An object containing the final `name`, `description`, and `shortDescription`.
 */
export const getTranslatedProductTexts = (
  product: RawProduct,
  translations: RawProductTranslations | {},
  currentLanguage: string,
  shouldTranslate: boolean
): TranslatedTexts => {
  let name = product.name;
  let description = product.description || "";
  let shortDescription = product.short_description || "";

  if (
    shouldTranslate &&
    "items" in translations === false && // Check it's not an empty array from the API
    translations[currentLanguage]
  ) {
    const translation = translations[currentLanguage];
    if (translation.name) name = translation.name;
    if (translation.description) description = translation.description;
    if (translation.short_description)
      shortDescription = translation.short_description;
  }

  // Decode HTML entities in the name (e.g. &#038; -> &)
  return {
    name: decodeHtmlEntities(name),
    description,
    shortDescription,
  };
};
